import { computed, readonly, ref } from 'vue'
import { userApi, type UsageOverview } from '@/api'
import { useAnalytics } from '@/composables/useAnalytics'

/**
 * Pro gating — the single source of truth for "can the current user do X?"
 * across the web app. Mirrors Android's `EntitlementService` / `PaywallController`.
 *
 * Four gates, all surfaced from `GET /me/usage`:
 *   classes (free: 3)  ·  scheme (free: 3)  ·  ai (free: 10/month)  ·  storage (free: 100 MB)
 *
 * Pro users are uncapped on the first three and get 5 GB storage. A `null`
 * limit from the API means "no cap" (Pro tier).
 *
 * The composable holds module-level reactive state so every screen sees the
 * same numbers. Call `refreshUsage()` after a creation succeeds or after a
 * successful checkout so caps update without a page reload.
 *
 * Triggering the paywall:
 *   import { useGating } from '@/composables/useGating'
 *   const { showPaywall } = useGating()
 *   showPaywall('classes')   // pops the ProPaywallDialog with the classes hero
 *
 * The dialog is mounted once at the app shell level and reads the same
 * `paywallGate` ref.
 */

/**
 * The four real caps + a `feature` fallback for non-quantitative Pro
 * triggers (PDF / Word export, AI gating before the count is loaded, etc).
 * `feature` shows the dialog with neutral hero copy and the same 4-gate
 * comparison underneath.
 */
export type PaywallGate = 'classes' | 'scheme' | 'ai' | 'storage' | 'feature'

/** Module-level state — shared across all consumers. */
const usage = ref<UsageOverview | null>(null)
const isLoading = ref(false)
const paywallGate = ref<PaywallGate | null>(null)
let inFlight: Promise<UsageOverview | null> | null = null

async function fetchUsage(force = false): Promise<UsageOverview | null> {
  if (!force && usage.value) return usage.value
  if (inFlight) return inFlight

  inFlight = (async () => {
    isLoading.value = true
    try {
      usage.value = await userApi.getUsageOverview()
      return usage.value
    } catch (e) {
      console.warn('Failed to load usage overview:', e)
      return null
    } finally {
      isLoading.value = false
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * `null` (no cap) → never at limit. Otherwise true once count >= limit.
 * Pro users always read `null` for the unlimited gates so this returns false.
 */
function isAtCap(count: number | undefined, limit: number | null | undefined): boolean {
  if (limit == null) return false
  return (count ?? 0) >= limit
}

export function useGating() {
  // Kick off the first fetch; subsequent calls are no-ops.
  fetchUsage()

  const { capture } = useAnalytics()

  const isPremium = computed(() => usage.value?.isPremium === true)

  const atClassCap = computed(() =>
    isAtCap(usage.value?.classesCount, usage.value?.classesLimit),
  )
  const atSchemeCap = computed(() =>
    isAtCap(usage.value?.planningSchemesCount, usage.value?.planningSchemesLimit),
  )
  const atAiCap = computed(() =>
    isAtCap(usage.value?.aiMonthlyUsed, usage.value?.aiMonthlyLimit),
  )
  const atStorageCap = computed(() => {
    const used = usage.value?.storageBytesUsed
    const limit = usage.value?.storageBytesLimit
    if (used == null || limit == null) return false
    return used >= limit
  })

  function isAtLimit(gate: PaywallGate): boolean {
    switch (gate) {
      case 'classes': return atClassCap.value
      case 'scheme': return atSchemeCap.value
      case 'ai': return atAiCap.value
      case 'storage': return atStorageCap.value
      case 'feature': return !isPremium.value
    }
  }

  function showPaywall(gate: PaywallGate) {
    paywallGate.value = gate
    // Free-tier cap events fire on every blocked attempt (not deduped) — an
    // intent signal distinct from the single pro_gate_shown impression.
    if (gate === 'classes' && atClassCap.value) {
      capture('class_limit_reached', {
        current_class_count: usage.value?.classesCount ?? 0,
        limit: usage.value?.classesLimit ?? 0,
      })
    } else if (gate === 'scheme' && atSchemeCap.value) {
      capture('course_limit_reached', {
        current_course_count: usage.value?.planningSchemesCount ?? 0,
        limit: usage.value?.planningSchemesLimit ?? 0,
      })
    }
  }

  function hidePaywall() {
    paywallGate.value = null
  }

  /**
   * Try to run [action]. If the user is already at [gate]'s cap, show the
   * paywall instead and don't call the action. The action can still hit a
   * server-side 402 on its own (race conditions, AI quota) — call
   * `handleQuotaError(gate, err)` from the catch block in that case.
   */
  function gated(gate: PaywallGate, action: () => void) {
    if (isAtLimit(gate)) {
      showPaywall(gate)
      return
    }
    action()
  }

  /**
   * Detects a 402 Payment Required response from the server (the standard
   * way the backend signals a quota exceeded after a race / hard limit) and
   * pops the matching paywall. Returns true if the error was handled.
   */
  function handleQuotaError(gate: PaywallGate, err: unknown): boolean {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 402) {
      showPaywall(gate)
      // Also refresh usage so the new count is reflected next time.
      void fetchUsage(true)
      return true
    }
    return false
  }

  return {
    // State
    usage: readonly(usage),
    isLoading: readonly(isLoading),
    paywallGate: readonly(paywallGate),

    // Derived
    isPremium,
    atClassCap,
    atSchemeCap,
    atAiCap,
    atStorageCap,

    // Actions
    refreshUsage: () => fetchUsage(true),
    isAtLimit,
    showPaywall,
    hidePaywall,
    gated,
    handleQuotaError,
  }
}

/**
 * Free-tier cap copy. Used by the paywall dialog + membership screen so we
 * have a single place to update if the backend defaults shift.
 */
export const FREE_LIMITS = {
  classes: 3,
  scheme: 3,
  ai: 10,
  storageMb: 100,
} as const

export const PRO_LIMITS = {
  storageGb: 5,
} as const
